'use strict'
const _ = require('lodash')
const workerUtils = require('../workerUtils')
const {bi, fedops, ACTION_NAMES} = require('../../utils/loggingUtils')
const {
    OPENED_EXPERIMENTS,
    ADDITIONAL_GHOST_CONTROLLERS,
    APP_STUDIO_WIDGETS_GHOSTABLE_URL,
    MEASURE_PERF,
    CSRF_TOKEN,
    APP_STUDIO_RESOLVED_GHOST_STRUCTURE
} = require('../../constants/store')
const {WIX_CODE_APP_DEF_ID} = require('../../constants/constants')
const {getGhostable} = require('../ghostModel')
const {getMinimalRouters, getAppRouters} = require('./handlerUtils')


function createInitHandler({store}) {
    function getControllersData({controllerConfigs, app, sdk, livePreviewOptions, routersConfigMap}) {
        return Object.keys(controllerConfigs).map(controllerId => {
            //when templateCompId exists, it should be the compId passed to the controller e.g in case the controller lives within a shared block
            const compId = controllerConfigs[controllerId].controllerData.templateCompId || controllerConfigs[controllerId].compId
            const type = controllerConfigs[controllerId].controllerData.controllerType
            const config = controllerConfigs[controllerId].controllerData.settings
            const externalId = controllerConfigs[controllerId].controllerData.externalId
            const connections = controllerConfigs[controllerId].connections || []
            const updateControllerData = _.isFunction(sdk.__INTERNAL__.updateControllerData) ? sdk.__INTERNAL__.updateControllerData.bind(null, controllerId) : null
            let warmupData = sdk.__INTERNAL__.getWarmupData && sdk.__INTERNAL__.getWarmupData(controllerId)
            warmupData = !self.isPseudoWorker ? workerUtils.decodeDates(warmupData) : warmupData
            const {appParams, platformAPIs, appDefId} = app
            if (appParams) {
                appParams.appRouters = getAppRouters({appDefId: app.appDefId, routersMap: routersConfigMap})
            }
            const resolveWixCodeApi = () => {
                if (appDefId !== WIX_CODE_APP_DEF_ID) {
                    return app.wixCodeApi
                }

                // wix code app runs user code on create controllers.
                // wix code app assumes it gets the extended wix code apis on init app for page.
                // app.wixCodeApi are always the extended wix code apis, i.e sdk.getScopedGlobalApis(appDefId)

                // 1. some namespaces identify wix code app by comparing the app def id to wix code app def id
                // https://github.com/wix-private/wix-code-client/blob/1ad7e6a0256540a26b963e1cc73b4cc6a1b33dcd/wix-code-viewer-app/src/app.js#L195
                const {events} = app.wixCodeApi
                // 2. we have self['wix-storage'] = platformUtils.storage in wix code app
                // https://github.com/wix-private/wix-code-client/blob/b3d276e2b3412427938aacc9da364aee6530078f/wix-code-viewer-app/src/app.js#L151
                const {storage} = platformAPIs
                // 3. in other cases we just have different sdks for wix code and for other apps
                const {site, location, window} = sdk
                // 4. last but not least, some namespaces identify wix code app by comparing the app def id to undefined
                const unscopedGlobalApis = sdk.getScopedGlobalApis()
                return {...unscopedGlobalApis, site, location, window, events, storage}
            }
            const wixCodeApi = resolveWixCodeApi()

            return {
                compId,
                type,
                config,
                externalId,
                connections,
                $w: sdk.getSelector(controllerId),
                warmupData,
                setProps: updateControllerData,
                appParams,
                livePreviewOptions,
                platformAPIs,
                wixCodeApi,
                csrfToken: store.getValue(CSRF_TOKEN)
            }
        })
    }

    function registerControllerEvents(sdk, controllerId, configs) { //TODO: Add context
        configs[controllerId].controllerBehaviors
            .filter(item => item.behavior)
            .forEach(item => {
                const fn = sdk.__INTERNAL__.getEventHandler(item.behavior.params.callbackId)
                if (fn) {
                    sdk.emitter.on(controllerId, item.action.name, fn)
                }
            })
    }

    function disposeOldControllers(app, livePreviewMode) {
        if (livePreviewMode) {
            const wrappedControllers = _.values(app.controllers).map(controller =>
                Promise.resolve(controller.dispose && controller.dispose())
            )
            return Promise.all(wrappedControllers)
        }
        return Promise.resolve()
    }

    function getControllersReadyResolver(app) {
        let controllersReady
        app.controllersReady = new Promise(resolve => {
            controllersReady = resolve
        })

        return controllersReady
    }

    function setControllersOnConfigUpdate(app, controllersOnReady) {
        app.controllersOnConfigUpdate = controllersOnReady
    }

    function callAppCreateControllers({app, controllerConfigs, workerId, sdk, livePreviewOptions, routersConfigMap}, reportError) {
        const controllerIds = Object.keys(controllerConfigs)
        if (!controllerIds.length) {
            return Promise.resolve([])
        }
        let controllers = []
        try {
            const createControllerArguments = getControllersData({controllerConfigs, app, workerId, sdk, livePreviewOptions, routersConfigMap})
            controllers = app.module.createControllers(createControllerArguments, app.controllerScriptMap)
        } catch (err) {
            reportError()(err)
            return Promise.reject(err)
        }
        const wrappedControllers = controllers.map((controller, index) => Promise.resolve(controller).catch(reportError(controllerIds[index])))
        return Promise.all(wrappedControllers)
    }

    function getControllersOnReady(controllers, sdk, controllerConfigs, app, workerId) {
        const controllerIds = Object.keys(controllerConfigs)
        app.controllers = controllerIds.reduce((acc, controllerId, index) => {
            const compId = controllerConfigs[controllerId].compId
            if (controllers[index]) {
                acc[compId] = controllers[index]
            }
            return acc
        }, {})
        return controllers.map((controller, index) => {
            const controllerId = controllerIds[index]
            const $w = sdk.getSelector(controllerId)
            const scopedGlobalSdkApis = sdk.getScopedGlobalApis(app.appDefId)
            return {
                controllerId,
                controllerConfigs,
                dependencies: controllerConfigs[controllerId].dependencies || [],
                onReady: $w.onReady.bind($w, () => {
                    if (!controller) {
                        return Promise.resolve()
                    }
                    if (controller.pageReady) {
                        const appId = app.appDefId
                        const {controllerType, controllerName} = controllerConfigs[controllerId].controllerData
                        const widgetId = controllerType
                        const is_rollout = app.appParams ? app.appParams.isRollout : 0
                        const widgetName = `${app.name} / ${controllerName || controllerType}`
                        const {reportInteractionStarted, reportInteractionEnded} = fedops.getPhaseReportFunctions({name: ACTION_NAMES.CONTROLLER_PAGE_READY, details: widgetName, params: {widgetId, appId, is_rollout}})
                        reportInteractionStarted()
                        const beforePageReady = Date.now()
                        return Promise
                            .resolve(controller.pageReady($w, scopedGlobalSdkApis))
                            .then(warmupData => {
                                const parsedWarmupData = self.isPseudoWorker ? workerUtils.encodeDates(warmupData) : warmupData
                                sdk.__INTERNAL__.setWarmupData(controllerId, parsedWarmupData)
                                reportInteractionEnded({duration: _.now() - beforePageReady})
                            })
                            .catch(err => {
                                bi.reportPlatformRenderError({
                                    appId,
                                    pageId: workerId,
                                    widgetId,
                                    error: err && err.message,
                                    name: ACTION_NAMES.PAGE_READY_FAILED,
                                    duration: Date.now() - beforePageReady
                                })
                            })
                    }
                    console.warn('controller.start is deprecated please export controller.pageReady method instead') // eslint-disable-line no-console
                    return controller.start($w)
                }),
                compId: controllerConfigs[controllerId].compId,
                onConfigUpdate: config => {
                    if (controller.updateConfig) {
                        controller.updateConfig($w, config)
                    }
                }
            }
        })
    }

    function callControllersReadyInOrder(controllersOnReady) {
        function visit(node) {
            if (node.entered || node.visited) {
                return
            }
            node.entered = true
            node.dependencies.forEach(controllerId => {
                const dep = _.find(controllersOnReady, {controllerId})
                if (dep) {
                    visit(dep)
                }
            })
            node.visited = true
            node.onReady()
        }
        let nextNode = _.find(controllersOnReady, node => !node.visited)
        while (nextNode) {
            visit(nextNode)
            nextNode = _.find(controllersOnReady, node => !node.visited)
        }
    }

    async function handleInit({apps, livePreviewMode, shouldSendMoreThanOnce, livePreviewOptions, routersConfigMap}, {workerId, getApp, sdk}) {
        if (!apps) {
            throw new Error('Init message data must include apps property')
        }
        const measure = store.getValue(MEASURE_PERF)
        measure.start('init')
        _.invoke(sdk, ['__INTERNAL__', 'addEnvParams'], {
            routersMap: getMinimalRouters(routersConfigMap)
        })

        const {ghostControllers, ghostComponents} = await getGhostable(apps,
            store.getValue(APP_STUDIO_WIDGETS_GHOSTABLE_URL),
            store.getValue(ADDITIONAL_GHOST_CONTROLLERS),
            store.getValue(OPENED_EXPERIMENTS).has('bv_ghostableUrl'))
        store.setValue(APP_STUDIO_RESOLVED_GHOST_STRUCTURE, ghostComponents)
        if (shouldSendMoreThanOnce) {
            _.invoke(sdk, ['__INTERNAL__', 'clearReadyManager'])
        }
        _.invoke(sdk, ['__INTERNAL__', 'addLivePreviewMode'], livePreviewMode)
        _.invoke(sdk, ['__INTERNAL__', 'setShouldClearState'], !livePreviewMode && shouldSendMoreThanOnce)
        const controllersOnReady = []
        return Promise.all(_.keys(apps)
            .map(appId => {
                const app = getApp(appId)
                if (!app) {
                    bi.reportPlatformRenderError({
                        name: ACTION_NAMES.HANDLE_INIT_FAILED,
                        appId,
                        error: `App with the id - ${appId} was not found in the app store`,
                        pageId: workerId
                    })
                    return Promise.resolve()
                }
                const ghostControllerConfigs = ghostControllers[appId] || {}
                const {controllers: controllerConfigs = {}} = apps[appId]
                const controllersReady = getControllersReadyResolver(app)
                const is_rollout = app.appParams ? app.appParams.isRollout : 0
                const widgetArray = _.keys(controllerConfigs).map(cId => controllerConfigs[cId].controllerData.controllerType).join(',')
                _.assign(controllerConfigs, ghostControllerConfigs)
                let beforeInit
                const {reportInteractionStarted, reportInteractionEnded} = fedops.getPhaseReportFunctions({
                    name: ACTION_NAMES.CREATE_CONTROLLERS,
                    details: app.name,
                    params: {appId: app.appDefId, widgetArray, is_rollout}
                })
                return disposeOldControllers(app, livePreviewMode)
                    .then(() => app.initAppForPageResult)
                    .then(() => {
                        reportInteractionStarted()
                        beforeInit = Date.now()
                        const reportError = controllerId => err => {
                            const params = {
                                appId,
                                pageId: workerId,
                                error: err && err.message,
                                duration: Date.now() - beforeInit,
                                name: ACTION_NAMES.CREATE_CONTROLLERS_FAILED
                            }
                            if (controllerId) {
                                params.controllerId = controllerId
                            }
                            bi.reportPlatformRenderError(params)
                        }
                        return callAppCreateControllers({app, controllerConfigs, workerId, sdk, livePreviewOptions, routersConfigMap}, reportError)
                    })
                    .then(controllers => {
                        reportInteractionEnded({duration: _.now() - beforeInit})
                        controllersReady()
                        controllersOnReady.push(...getControllersOnReady(controllers, sdk, controllerConfigs, app, workerId))
                        setControllersOnConfigUpdate(app, controllersOnReady)
                    })
                    .catch(e => {
                        /*eslint-disable no-console*/
                        console.error(e)
                        controllersReady()
                    })
            }))
            .then(() => {
                controllersOnReady.forEach(controller => registerControllerEvents(sdk, controller.controllerId, controller.controllerConfigs))
                callControllersReadyInOrder(controllersOnReady)
            })
            .then(() => measure.end('init'))
    }

    return handleInit
}

module.exports = createInitHandler
